/**
 * Analytics Portal V1 — the Resource metric map (Phase 15, plan §4.7).
 *
 * Only keys the Phase 8 `GET /api/analytics/resource` payload contains, and never a value this file
 * computes. **Visit Resource** is the primary Resource conversion and is reported on its own, while
 * **Share Actions** is the server's total of Copy Link + Share with Connections and never includes a
 * Visit Resource click (Decision 7). Related-resource clicks are unsupported (C4) and have no entry
 * here, so they can never render. `search_ctr` is present in the payload but deliberately not
 * rendered in V1 (plan §12.12 A11-2).
 */
export const RESOURCE_HEADLINE = [
  { key: "visits", label: "Resource page visits", hint: "One visit per session." },
  { key: "unique_visitors", label: "Unique visitors" },
  { key: "returning_visitors", label: "Returning visitors" },
  { key: "engagement_rate", label: "Engagement rate", suffix: "%" },
];

export const RESOURCE_GROUPS = [
  {
    id: "discovery",
    title: "Search & resource-directory discovery",
    description: "How people came across this resource.",
    metrics: [
      { key: "search_impressions", label: "Search impressions" },
      { key: "search_clicks", label: "Search clicks" },
      { key: "unique_search_clickers", label: "Unique search clickers" },
      { key: "directory_impressions", label: "Resource-directory impressions" },
      { key: "directory_clicks", label: "Resource-directory result clicks" },
      { key: "unique_directory_clickers", label: "Unique directory clickers" },
    ],
  },
  {
    id: "outbound",
    title: "Visit Resource",
    description: "A Visit Resource click is the primary conversion for a resource: someone clicked "
      + "through from this page to the resource itself.",
    metrics: [
      { key: "visit_clicks", label: "Visit Resource clicks" },
      { key: "unique_visit_clickers", label: "Unique Visit Resource clickers" },
      { key: "conversion_rate", label: "Visit-to-resource conversion", suffix: "%" },
    ],
  },
  {
    id: "sharing",
    title: "Sharing",
    description: "Share Actions is Copy Link plus Share with Connections. It does not include "
      + "Visit Resource clicks, and individual sharers are never recorded or shown.",
    metrics: [
      { key: "copy_link", label: "Copy Link actions" },
      { key: "shared_with_connections", label: "Share with Connections" },
      { key: "share_actions", label: "Share Actions" },
    ],
  },
  {
    id: "bookmarks",
    title: "Bookmarks",
    description: "The current total is live. Removals and net change are measured from "
      + "instrumentation, so each card states the date it has been measured from.",
    metrics: [
      { key: "bookmarks_current", label: "Bookmarks now" },
      { key: "bookmarks_added", label: "Bookmarks added" },
      { key: "bookmarks_removed", label: "Bookmarks removed" },
      { key: "bookmarks_net", label: "Net bookmarks" },
    ],
  },
  {
    id: "messaging",
    title: "Comments & questions",
    description: "Counts and metadata only — never comment, question or answer content.",
    metrics: [
      { key: "comments", label: "Comments" },
      { key: "questions", label: "Questions received" },
      { key: "questions_answered", label: "Questions answered" },
    ],
  },
  {
    id: "engagement",
    title: "Engagement",
    description: "Internal link clicks are an aggregate total — no destination id, URL or visitor "
      + "identity is recorded or displayed.",
    metrics: [
      { key: "engagement_actions", label: "Engagement actions" },
      { key: "unique_engagers", label: "Unique engagers" },
      { key: "internal_clicks", label: "Internal link clicks" },
    ],
  },
];
